import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { useNetworkIdRedirect } from "./common/hooks";
import { Container, Row, Col, Tab, Button, Form, Nav, Breadcrumb } from "react-bootstrap";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import TabTableWithSearch from './TabTableWithSearch';
import refreshIcon from "assets/svg/refresh-green.svg";
import { IconButton } from "@mui/material";
import { SensorTypeDropdown } from "components/gateways/SensorTypeDropdown";
import { ActuatorTypeDropdown } from "./common/ActuatorTypeDropdown";
import SensorIcon from "components/gateways/SensorIcon";
import { TimeRangeSelection } from "components/common/TimeRangeDropdown";
import moment from "moment";
import { CHART_CUSTOM_RANGE_OPTION } from "constant";
import { 
    getGatewayAlertNotification,
    getOneRegistry,
    getRegistryConfig,
    getGwAndLDSUStatus,
    getAllLastBusReading,
    getSensorLastReading
} from "service/gatewayService";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import { WalletAlertComponent, walletModalTrigger } from "hooks/wallet";
import { useCheckIfBasicPlan } from "hooks/useCheckIfBasicPlan";
import { LoadingModal } from "components/modals/ModalTemplate";
import { isHttpSuccess, convertSecondsToPrometheusTime } from "utils/functions";
import { get } from "lodash";
import { useOrganisation } from "hooks/organization";
import { getLDSBusInfo } from "utils/gatewayFunctions";
import { getCalendarFormat } from "utils/datetime";
import {
    getTagDeviceSAID,
    getTagDeviceUID,
    isRoomOrZoneByName,
    isGroupOrHubByName,
    isSensorFromEntityDevice,
    getIOControllerActuatorsFromConfig,
    selectEntityUUID,
} from "./common/deviceUtils";
import { getSensorsControllersTabType, renderOnOffHighlightedCommon } from "./common/tabUtils";
import { getControllerStatusInfo } from "./common/controllerStatus";
import { getControllerStatusFromReading2 } from "./common/controllerReadingUtils";
import { renderStatusCellCommon, renderLastActiveCellCommon } from "./common/statusRenderer";
import {
    queryTagStatusFromUIDSAIDsWithTimestamp,
} from "service/networkService";
import { buildPromQLQueryFromUIDSAIDsMap, selectUIDSAIDsForUID } from "./common/promqlUtils";
import {
    createDefaultLast24HoursTimeRange,
    getPrometheusTimeRangeForSensorReadings,
    pickBestSensorBusReadingForSAID,
    mergeSensorLastReadingWithMinMaxFallback,
} from "./common/sensorStatsUtils";
import {
    formatSensorReadingCellValue,
    formatSensorMinMaxCellValue,
    getSensorUnit,
} from "./common/sensorValueFormatter";
import { fetchTags as fetchTagsCommon } from "./common/tagService";
import { 
    updateAlertStatus as updateAlertStatusCommon,
    returnCheckboxStatus as returnCheckboxStatusCommon,
    returnDisabledStatus as returnDisabledStatusCommon
} from "./common/alertUtils";
import { getRowToForContext, type NetworkRowToContext } from "./common/getRowTo";
import { createSensorNameRenderer as createCommonSensorNameRenderer } from "./common/sensorRenderers";
import { getNetworkColumnLabel } from "./common/columnLabelUtils";

type ColumnRenderer = (row: any) => React.ReactNode;

const createControllerNameRenderer = (
    columnKey: string,
    tabKey: string,
    onRowClick: (row: any, tabKey: string) => void,
    getRowTo?: (row: any, tabKey: string) => string
): ColumnRenderer => {
    return (row: any) => {
        const to = getRowTo?.(row, tabKey) ?? "#";
        return (
            <Link
                to={to}
                onClick={to === "#" ? (e) => {
                    e.preventDefault();
                    onRowClick(row, tabKey);
                } : undefined}
            >
                {row[columnKey]}
            </Link>
        );
    };
};

const createReadingRenderer = (col: string): ColumnRenderer => {
    return (row: any) => {
        // Align with NetworkDetails: format from raw readings value, not the preformatted cell,
        // so that display matches the numeric value used for sorting.
        return (
            <span style={{ color: "#00d1b2" }}>
                {formatSensorReadingCellValue(row, row[col])}
            </span>
        );
    };
};

const createMinMaxRenderer = (col: string): ColumnRenderer => {
    return (row: any) => (
        <span>
            {row[col] || "-"}
        </span>
    );
};

const createLastActiveRenderer = (
    col: string,
    tabKey: string,
    onRefreshSensor: (
        e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
        row: any
    ) => void
): ColumnRenderer => {
    return (row: any) =>
        renderLastActiveCellCommon({
            row,
            col,
            tabKey,
            onRefreshSensor,
        });
};

const createStatusRenderer = (
    col: string,
    tabKey: string,
    onRefreshController: (e: React.MouseEvent, controllerName: string) => void,
    renderOnOffHighlighted: (text: string) => React.ReactNode
): ColumnRenderer => {
    return (row: any) => {
        const statusValue = row[col];
        return renderStatusCellCommon({
            statusValue,
            tabKey,
            controllerName: row.name,
            onRefreshController,
            renderOnOffHighlighted,
            refreshButtonExtraClassName: "refresh-controller",
        });
    };
};

const createAlertRenderer = (
    col: string,
    onToggleAlert: (ldsuUuid: string, said: number) => void,
    isChecked: (ldsuUuid: string, said: number) => boolean,
    isDisabled: (ldsuUuid: string, said: number) => boolean
): ColumnRenderer => {
    return (row: any) => (
        <HoverAuthorizeTooltip permission="gateway:update">
            <div>
                <Form.Check
                    type="switch"
                    id={`switch-${row.ldsu_uuid}-${row.SAID}`}
                    label=""
                    checked={isChecked(row.ldsu_uuid, row.SAID)}
                    disabled={isDisabled(row.ldsu_uuid, row.SAID)}
                    onChange={() => onToggleAlert(row.ldsu_uuid, row.SAID)}
                />
            </div>
        </HoverAuthorizeTooltip>
    );
};

const tabKeys = [
    "sensors",
    "controllers",
];

const tabLabels: { [key: string]: string } = {
    sensors: "Sensors in this Hub",
    controllers: "Actuators in this Hub",
};

interface HubDetailsProps {
    networkId?: string;
    hubId?: string;
    showBreadcrumbs?: boolean;
    customTitle?: string;
}

const HubDetails = ({ 
    networkId, 
    hubId, 
    showBreadcrumbs = true,
    customTitle 
}: HubDetailsProps): JSX.Element => {
    const params = useParams<{ id: string; hubId: string }>();
    
    const id = networkId || params.id;
    const hubIdFromParams = hubId || params.hubId;
    
    const [activeTab, setActiveTab] = useState("sensors");
    const [hub, setHub] = useState<any>(null);
    const [hubName, setHubName] = useState(hubIdFromParams || "");
    const [isHubNameEditing, setIsHubNameEditing] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [sensors, setSensors] = useState<any[]>([]);
    const [controllers, setControllers] = useState<any[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const history = useHistory();
    const { currentOrgId } = useOrganisation();
    const hubTagUIDSAIDsRef = useRef<Map<string, Set<number>>>(new Map());
    useNetworkIdRedirect(id);
    
    // Sensor-specific state
    const [selectedSensorTypes, setSelectedSensorTypes] = useState<string[]>([]);
    const [selectedActuatorTypes, setSelectedActuatorTypes] = useState<string[]>([]);
    const timeRange = useMemo<TimeRangeSelection>(
        () => createDefaultLast24HoursTimeRange(),
        []
    );

    // Alert state management
    const [alertUUIDList, setAlertUUIDList] = useState<any[]>([]);
    const [isAlertLoading, setIsAlertLoading] = useState(false);
    const { IS_BASIC_PLAN } = useCheckIfBasicPlan();

    // Helper to render a timestamp in a consistent calendar format
    const renderDateTime = (timestamp?: number | string): string => {
        if (!timestamp) {
            return "";
        }
        return moment(timestamp, "X").calendar(getCalendarFormat());
    };

    // Process sensors for display
    const processedSensors = useMemo(() => {
        return sensors.map((sensor: any) => {
            // Extract minmax from sensor to avoid duplicate column
            const { minmax, ...sensorWithoutMinmax } = sensor;
            const ldsuUUID = sensor.ldsu_uuid || sensor.UID || "";
            const saidValue = sensor.SAID || 0;
            const resolvedUnit = getSensorUnit(sensor);
            return {
                // Display fields
                name: sensor.sensor_name || sensor.name || "-",
                sensor_name: sensor.sensor_name || sensor.name || "-",
                sensor_type: sensor.sensor_type || sensor.type || "-",
                type: sensor.sensor_type || sensor.type || "-",
                // Raw reading value for display; actual formatting happens in the column renderer,
                // similar to NetworkDetails, so sorting and display are consistent.
                reading: sensor.readings?.value ?? "-",
                // Numeric value for sorting; use Infinity so "-" (no reading) sorts last in asc, first in desc
                readingSortValue:
                    sensor.readings?.value != null &&
                    sensor.readings?.value !== "-" &&
                    !Number.isNaN(Number(sensor.readings.value))
                        ? Number(sensor.readings.value)
                        : Number.POSITIVE_INFINITY,
                minMax: sensor.readings?.minmax
                    ? formatSensorMinMaxCellValue({
                        readings: sensor.readings,
                        accuracy: sensor.accuracy,
                        unit: resolvedUnit,
                    })
                    : "-",
                minMaxSortValue: (() => {
                    const arr = sensor.readings?.minmax;
                    if (!arr || !Array.isArray(arr) || arr.length < 1) return Number.POSITIVE_INFINITY;
                    const v = arr[0];
                    if (v == null || v === "-" || Number.isNaN(Number(v))) return Number.POSITIVE_INFINITY;
                    return Number(v);
                })(),
                lastActive: renderDateTime(sensor.readings?.timestamp),
                ldsu_uuid: ldsuUUID,
                SAID: saidValue,
                ldsu_name: sensor.ldsu_name || sensor.name || "-",
                alert: "",
                // For alert column sorting: 1 if enabled, 0 if disabled
                alertSortValue: returnCheckboxStatusCommon(
                    alertUUIDList as any,
                    ldsuUUID,
                    saidValue
                )
                    ? 1
                    : 0,
                accuracy: sensor.accuracy,
                unit: resolvedUnit,
                readings: sensor.readings,
                ...sensorWithoutMinmax
            };
        });
    }, [sensors, alertUUIDList]);

    // Filter sensors by selected types
    const filteredSensorList = useMemo(() => {
        if (!processedSensors.length || selectedSensorTypes.length === 0) {
            return processedSensors;
        }
        return processedSensors.filter(sensor => 
            selectedSensorTypes.includes(sensor.type)
        );
    }, [processedSensors, selectedSensorTypes]);
    
    // Sensor types for filtering
    const sensorTypes = useMemo(() => {
        if (activeTab !== "sensors" || !processedSensors || processedSensors.length === 0) return [];
        const types: { [id: string]: string } = {};
        for (const row of processedSensors) {
            const type = row.type;
            if (type && !types[type]) {
                types[type] = type;
            }
        }
        return Object.keys(types).map((id) => ({ id, name: id }));
    }, [processedSensors, activeTab]);

    // Derive actuator types for filtering
    const actuatorTypes = useMemo(() => {
        if (!controllers.length) return [];
        const types: { [id: string]: string } = {};
        for (const row of controllers) {
            const type = row.type;
            if (type && !types[type]) {
                types[type] = type;
            }
        }
        return Object.keys(types).map((id) => ({ id, name: id }));
    }, [controllers]);

    // Filter controllers by selected actuator types
    const filteredControllers = useMemo(() => {
        if (!controllers.length || selectedActuatorTypes.length === 0) {
            return controllers;
        }
        return controllers.filter((actuator: any) =>
            selectedActuatorTypes.includes(actuator.type)
        );
    }, [controllers, selectedActuatorTypes]);

    // Process controllers for display (status from last bus reading only)
    const processedControllers = useMemo(() => {
        return filteredControllers.map((actuator: any) => {
            const { status } = getControllerStatusInfo(actuator);
            const lastActiveTs = actuator.readings?.timestamp ?? actuator.timestamp;
            return {
                name: actuator.name || "-",
                type: actuator.type || "-",
                status,
                lastActive: renderDateTime(lastActiveTs),
                UID: actuator.UID,
                said: actuator.said,
            };
        });
    }, [filteredControllers]);

    // Alert management functions
    const fetchAlertData = useCallback(async () => {
        const ldsuGatewayId = id; // Use panlNetworkId from route params
        
        try {
            const alertResponse = await getGatewayAlertNotification(ldsuGatewayId);
            if (isHttpSuccess(alertResponse.status)) {
                const alerts = alertResponse.data || [];
                // Transform alert data to match sensor structure (device_id -> source, enabled -> status)
                const transformedAlerts = alerts.map((alert: any) => ({
                    uuid: alert.uuid,
                    source: alert.device_id,
                    said: alert.said,
                    gatewayUUID: alert.gateway_id,
                    status: alert.enabled,
                    notice: alert.notice,
                }));
                setAlertUUIDList(transformedAlerts);
            }
        } catch (error) {
            console.error("Error fetching alert data:", error);
        }
    }, [id]);

    const updateAlertStatus = async (source: string, said: number) => {
        await updateAlertStatusCommon(
            alertUUIDList as any,
            source,
            String(said),
            setAlertUUIDList as any,
            setIsAlertLoading
        );
    };

    const returnCheckboxStatus = (source: string, said: number) => {
        return returnCheckboxStatusCommon(alertUUIDList as any, source, said);
    };

    const returnDisabledStatus = (source: string, said: number) => {
        return returnDisabledStatusCommon(alertUUIDList as any, source, said);
    };

    /**
     * Fetches tags from API
     * @param organizationId - Organization ID
     * @param tagValue - Optional tag value filter (e.g., hub name)
     * @returns Array of tag objects
     */
    const fetchTags = async (organizationId: string, tagValue?: string) =>
        fetchTagsCommon(organizationId, { value: tagValue });

    const extractTagUIDs = (tags: any[]): Set<string> => {
        const uids = new Set<string>();
        for (const tag of tags) {
            if (tag.devices && Array.isArray(tag.devices)) {
                for (const device of tag.devices) {
                    const uid = getTagDeviceUID(device);
                    if (uid) {
                        uids.add(uid);
                    }
                }
            }
        }
        return uids;
    };

    const extractTagUIDSAIDs = (tags: any[]): Map<string, Set<number>> => {
        const uidSaidMap = new Map<string, Set<number>>();

        const addDeviceToMap = (device: any) => {
            const uid = getTagDeviceUID(device);
            if (!uid) {
                return;
            }

            const said = getTagDeviceSAID(device);
            let saidSet = uidSaidMap.get(uid);

            if (!saidSet) {
                saidSet = new Set<number>();
                uidSaidMap.set(uid, saidSet);
            }

            saidSet.add(said);
        };

        for (const tag of tags) {
            if (tag.devices && Array.isArray(tag.devices)) {
                for (const device of tag.devices) {
                    addDeviceToMap(device);
                }
            }
        }
        return uidSaidMap;
    };

    /**
     * Extracts device UIDs from registry API response
     * @param registryData - Registry API response data
     * @returns Set of device UIDs
     */
    const extractRegistryUIDs = (registryData: any): Set<string> => {
        const uids = new Set<string>();
        if (registryData?.info?.devices && Array.isArray(registryData.info.devices)) {
            for (const device of registryData.info.devices) {
                if (device?.UID) {
                    uids.add(String(device.UID));
                } else if (Array.isArray(device) && device.length > 0) {
                    uids.add(String(device[0]));
                } else if (typeof device === "string") {
                    uids.add(device);
                }
            }
        }
        return uids;
    };

    const findHubUIDs = (hubTagUIDs: Set<string>, registryUIDs: Set<string>): Set<string> => {
        const hubUIDs = new Set<string>();
        for (const uid of Array.from(hubTagUIDs)) {
            if (registryUIDs.has(uid)) {
                hubUIDs.add(uid);
            }
        }
        return hubUIDs;
    };

    const collectBusDevices = (
        devices: any[],
        configList: any[],
        statusDevices: any[]
    ): { sensors: any[]; actuators: any[] } => {
        const allSensors: any[] = [];
        const allActuators: any[] = [];

        for (let busNumber = 1; busNumber <= 3; busNumber++) {
            const { sensors: busSensors, actuators: busActuators } = getLDSBusInfo(
                busNumber.toString(),
                devices,
                configList,
                statusDevices
            );

            // Filter out only sensors from entity devices (zone/room/group/hub). Include VIRTUAL/aux-hub sensors.
            const actualSensors = busSensors.filter((s: any) => !isSensorFromEntityDevice(s, devices));

            // Merge base actuators from LDSU with IO Controller actuators built from config
            const ioControllerActuators = getIOControllerActuatorsFromConfig(
                busNumber.toString(),
                devices,
                configList,
                statusDevices
            );

            const actuatorMap = new Map<string, any>();
            const addActuator = (actuator: any) => {
                const key = `${actuator.UID}:${actuator.said ?? actuator.SAID ?? 0}`;
                if (!actuatorMap.has(key)) {
                    actuatorMap.set(key, actuator);
                }
            };

            (busActuators || []).forEach(addActuator);
            (ioControllerActuators || []).forEach(addActuator);

            const mergedActuators = Array.from(actuatorMap.values());

            // At hub level, trust per‑channel CLS/type from getLDSBusInfo /
            // getActuatorList and do not re‑filter by device OBJ. This avoids
            // hiding valid actuator channels on auxiliary hubs.
            const filteredActuators = mergedActuators;

            allSensors.push(...actualSensors);
            allActuators.push(...filteredActuators);
        }

        return { sensors: allSensors, actuators: allActuators };
    };

    // Build UID to device map (includes OBJ, NAME, NICK for identification)
    const buildUIDToDeviceMap = (devices: any[]): Map<string, any> => {
        const uidToDeviceMap = new Map<string, any>();
        for (const device of devices) {
            const deviceUID = device?.UID || device?.uid || "";
            if (deviceUID) {
                uidToDeviceMap.set(String(deviceUID), device);
            }
        }
        return uidToDeviceMap;
    };

    const filterHubSensors = (
        sensorsWithReadings: any[],
        hubUIDs: Set<string>,
        hubTagUIDSAIDs: Map<string, Set<number>>,
        uidToDeviceMap: Map<string, any>
    ): any[] => {
        return sensorsWithReadings.filter((sensor: any) => {
            const sensorUID = sensor.ldsu_uuid || sensor.UID || "";
            const sensorSAID = sensor.SAID !== undefined && sensor.SAID !== null ? Number(sensor.SAID) : 0;

            if (!hubUIDs.has(sensorUID)) {
                return false;
            }

            const allowedSAIDs = hubTagUIDSAIDs.get(sensorUID);
            if (allowedSAIDs && !allowedSAIDs.has(sensorSAID)) {
                return false;
            }

            const device = uidToDeviceMap.get(sensorUID);
            if (!device) {
                return false;
            }

            // Check NAME/NICK field first (more reliable when same UID appears in tags API)
            const name = device?.NAME;
            const nick = device?.NICK;
            if (isGroupOrHubByName(name) || isGroupOrHubByName(nick)) {
                // Exclude groups/hubs
                return false;
            }
            if (isRoomOrZoneByName(name) || isRoomOrZoneByName(nick)) {
                // Exclude rooms/zones
                return false;
            }

            // At hub level, trust per‑channel CLS/type from getLDSBusInfo and
            // do not additionally filter by OBJ for auxiliary hubs; this keeps
            // auxiliary‑hub sensors visible when they are explicitly tagged.
            return true;
        });
    };

    const filterHubActuators = (
        allActuators: any[],
        hubUIDs: Set<string>,
        hubTagUIDSAIDs: Map<string, Set<number>>,
        uidToDeviceMap: Map<string, any>
    ): any[] => {
        return allActuators.filter((actuator: any) => {
            const actuatorUID = actuator.UID || "";
            const actuatorSAID = actuator.said !== undefined && actuator.said !== null ? Number(actuator.said) : 0;

            if (!hubUIDs.has(actuatorUID)) {
                return false;
            }

            const allowedSAIDs = hubTagUIDSAIDs.get(actuatorUID);
            if (allowedSAIDs && allowedSAIDs.size > 0 && !allowedSAIDs.has(actuatorSAID)) {
                return false;
            }

            const device = uidToDeviceMap.get(actuatorUID);
            if (!device) {
                return false;
            }

            // Check NAME/NICK field first (more reliable when same UID appears in tags API)
            const name = device?.NAME;
            const nick = device?.NICK;
            if (isGroupOrHubByName(name) || isGroupOrHubByName(nick)) {
                // Exclude groups/hubs
                return false;
            }
            if (isRoomOrZoneByName(name) || isRoomOrZoneByName(nick)) {
                // Exclude rooms/zones
                return false;
            }

            // Fallback to OBJ value check
            // For hubs, include actuators from auxiliary hub devices (e.g. IO Controller outputs)
            // when they are explicitly tagged on the hub. Only exclude explicit group/room/zone
            // entity devices above.
            return true;
        });
    };

    const getHubStatus = async (
        networkId: string,
        orgId: string,
        hubUuid?: string | null
    ): Promise<string> => {
        if (!orgId || !networkId || hubTagUIDSAIDsRef.current.size === 0) {
            return "Unknown";
        }

        try {
            const hubUIDSAIDsForStatus = selectUIDSAIDsForUID(hubTagUIDSAIDsRef.current, hubUuid);
            const { status } = await queryTagStatusFromUIDSAIDsWithTimestamp(
                networkId,
                (gw: string) =>
                    buildPromQLQueryFromUIDSAIDsMap(gw, hubUIDSAIDsForStatus)
            );
            if (status === "On") {
                return "Online";
            } else if (status === "Off") {
                return "Offline";
            }
            return "Unknown";
        } catch (error) {
            console.error("Error querying hub status by UID:", error);
            return "Unknown";
        }
    };

    // Map readings to sensors (helper function for refresh)
    const mapSensorsWithReadings = useCallback(async (sensorsList: any[], timeRange?: string, offset?: string) => {
        if (!id || !sensorsList.length) return sensorsList;
        
        try {
            const readingPromises = [];
            for (let busNumber = 1; busNumber <= 3; busNumber++) {
                readingPromises.push(
                    getAllLastBusReading(
                        id,
                        busNumber.toString(),
                        timeRange,
                        offset
                    )
                );
            }
            const readingResults = await Promise.all(readingPromises);
            const allReadings: any[] = [];
            for (const result of readingResults) {
                if (isHttpSuccess(result.status)) {
                    allReadings.push(...(result.data?.devices || []));
                }
            }

            return sensorsList.map((sensor: any) => {
                const bestReading = pickBestSensorBusReadingForSAID(
                    sensor,
                    allReadings
                );

                if (bestReading) {
                    sensor.readings = {
                        value: bestReading.value,
                        highest: bestReading.highest,
                        lowest: bestReading.lowest,
                        timestamp: bestReading.timestamp,
                        minmax: [bestReading.highest, bestReading.lowest],
                    };
                }

                return sensor;
            });
        } catch (error) {
            console.error("HubDetails: Error mapping sensor readings:", error);
            return sensorsList;
        }
    }, [id]);

    // Fetch hub data
    const fetchHubData = useCallback(async () => {
        if (!id || id === 'undefined' || !hubIdFromParams || !currentOrgId) {
            setIsPageLoading(false);
            return;
        }

        try {
            setIsPageLoading(true);
            const expectedTagValue = `hub/${hubIdFromParams}`;
            const rawHubTags = await fetchTags(currentOrgId, expectedTagValue);
            const hubTags = Array.isArray(rawHubTags)
                ? rawHubTags.filter((tag: any) => tag?.value === expectedTagValue)
                : [];
            const hubTagUIDs = extractTagUIDs(hubTags);
            const hubTagUIDSAIDs = extractTagUIDSAIDs(hubTags);
            hubTagUIDSAIDsRef.current = hubTagUIDSAIDs;
            
            const [registryRes, configRes, statusRes] = await Promise.all([
                getOneRegistry(id),
                getRegistryConfig(id),
                getGwAndLDSUStatus(id)
            ]);

            if (!isHttpSuccess(registryRes.status) || 
                !isHttpSuccess(configRes.status) || 
                !isHttpSuccess(statusRes.status)) {
                setIsPageLoading(false);
                return;
            }

            const devices = registryRes.data?.info?.devices || [];
            const configList = configRes.data || [];
            const statusDevices = statusRes.data?.devices || [];
            const registryUIDs = extractRegistryUIDs(registryRes.data);
            
            const hubUIDs = findHubUIDs(hubTagUIDs, registryUIDs);

            const { sensors: allSensors, actuators: allActuators } = collectBusDevices(
                devices,
                configList,
                statusDevices
            );

            const selectedTimeRange = timeRange;
            let lastTimeValue = Math.round(
                moment.duration(selectedTimeRange.endDate.diff(selectedTimeRange.startDate)).asSeconds()
            );
            let offsetTime: string | undefined;

            if (selectedTimeRange.range === CHART_CUSTOM_RANGE_OPTION) {
                lastTimeValue = Math.round(
                    moment.duration(moment().diff(selectedTimeRange.endDate)).asSeconds()
                );

                offsetTime = convertSecondsToPrometheusTime(lastTimeValue);
                lastTimeValue = moment
                    .duration(selectedTimeRange.endDate.diff(selectedTimeRange.startDate))
                    .asSeconds();
            }

            const lastTime = convertSecondsToPrometheusTime(lastTimeValue);

            const sensorsWithReadings = await mapSensorsWithReadings(
                allSensors,
                lastTime,
                offsetTime
            );
            const uidToDeviceMap = buildUIDToDeviceMap(devices);
            
            const hubSensors = filterHubSensors(
                sensorsWithReadings || [],
                hubUIDs,
                hubTagUIDSAIDs,
                uidToDeviceMap
            );
            
            const hubActuators = filterHubActuators(
                allActuators,
                hubUIDs,
                hubTagUIDSAIDs,
                uidToDeviceMap
            );

            const hubUUID = selectEntityUUID(hubUIDs, devices, hubIdFromParams, "hub");

            // Find hub device in registry for additional device info (serial number, MAC address, etc.)
            const hubDevice = hubUUID
                ? devices.find((device: any) => {
                      const deviceUID = device?.UID || device?.uid || "";
                      return String(deviceUID) === hubUUID;
                  })
                : undefined;

            const hubStatus = await getHubStatus(id, currentOrgId, hubUUID);

            const hubData = {
                name: hubIdFromParams,
                // Use the same per‑channel, tag‑scoped lists as the tables
                // so header counts always match the detailed view.
                sensors: hubSensors.length,
                controllers: hubActuators.length,
                status: hubStatus,
                lastActive: "Unknown",
                uuid: hubUUID || "-",
                serialNumber: hubDevice?.SNO || hubDevice?.serialNumber || "-",
                ethernetMacAddress: hubDevice?.PMAC || hubDevice?.ethernetMacAddress || "-",
                wifiMacAddress: hubDevice?.wifiMacAddress || "-",
                modelNumber: hubDevice?.model || hubDevice?.modelNumber || "-",
                productVersion: hubDevice?.PRV || hubDevice?.productVersion || "-",
                firmwareVersion: hubDevice?.FWV || hubDevice?.firmwareVersion || "-"
            };

            setHub(hubData);
            setHubName(hubIdFromParams);
            setSensors(hubSensors);
            // Merge actuator status from getAllLastBusReading (same source as refresh)
            const mergeActuatorWithDevices = (a: any, devices: any[]) => {
                const device = devices.find((d: any) => d.UID === a.UID);
                if (!device) return a;
                const status = get(device.value, `[${a.said || 0}]`, null);
                return { ...a, readings: { status, timestamp: device.ts } };
            };
            const mergeActuatorsWithReadings = async (actuatorsList: any[]) => {
                const readingPromises = [];
                for (let busNumber = 1; busNumber <= 3; busNumber++) {
                    readingPromises.push(getAllLastBusReading(id, busNumber.toString()));
                }
                const results = await Promise.all(readingPromises);
                const allDevices: any[] = [];
                for (const r of results) {
                    if (isHttpSuccess(r.status) && r.data?.devices) allDevices.push(...r.data.devices);
                }
                return actuatorsList.map((a) => mergeActuatorWithDevices(a, allDevices));
            };
            const hubActuatorsWithReadings = await mergeActuatorsWithReadings(hubActuators);
            setControllers(hubActuatorsWithReadings);

            // Fetch alert data
            await fetchAlertData();
            setIsPageLoading(false);
        } catch (error) {
            console.error("Error fetching hub data:", error);
            setIsPageLoading(false);
        }
    }, [id, hubIdFromParams, currentOrgId, fetchAlertData, mapSensorsWithReadings, timeRange]);

    // Dedicated function to update only the status of the hub
    const updateHubStatus = useCallback(async () => {
        if (!id || !hubIdFromParams || !currentOrgId) {
            return;
        }

        setIsRefreshing(true);
        try {
            const status = await getHubStatus(id, currentOrgId, hub?.uuid);
            setHub((prevHub: any) => {
                if (!prevHub) return prevHub;
                return {
                    ...prevHub,
                    status,
                };
            });
        } catch (error) {
            console.error("Error updating hub status:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [id, hubIdFromParams, currentOrgId, hub]);

    useEffect(() => {
        setIsPageLoading(true);
        fetchHubData();
    }, [fetchHubData]);

    // Refresh all sensor readings
    const refreshAllSensorReadings = useCallback(async (timeRange?: string, offset?: string) => {
        if (!id || !sensors.length) return;
        
        setIsRefreshing(true);
        try {
            const sensorsWithReadings = await mapSensorsWithReadings(
                sensors,
                timeRange,
                offset
            );
            setSensors(sensorsWithReadings);
        } catch (error) {
            console.error("HubDetails: Error refreshing sensor readings:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [id, sensors, mapSensorsWithReadings, setIsRefreshing, setSensors]);

    // Refresh a single sensor's reading using getSensorLastReading
    const refreshOneSensor = async (ldsuUUID: string, SAID: number) => {
        if (!id || !ldsuUUID) return;

        try {
            const { lastTime, offsetTime } =
                getPrometheusTimeRangeForSensorReadings(timeRange);
            const { data, status } = await getSensorLastReading(
                id,
                ldsuUUID,
                lastTime,
                offsetTime
            );

            if (isHttpSuccess(status)) {
                const rawValueCandidateRaw = get(data.value, `[${SAID}]`, "-");
                const highestCandidateRaw = get(data.max, `[${SAID}]`, "-");
                const lowestCandidateRaw = get(data.min, `[${SAID}]`, "-");

                const valueCandidate = rawValueCandidateRaw ?? "-";
                const highestCandidate = highestCandidateRaw ?? "-";
                const lowestCandidate = lowestCandidateRaw ?? "-";

                setSensors((prevSensors: any[]) => {
                    return prevSensors.map((s: any) => {
                        if (String(s.ldsu_uuid) === String(ldsuUUID) && String(s.SAID) === String(SAID)) {
                            const prevReadings = s.readings ?? {};

                            const updatedReading =
                                mergeSensorLastReadingWithMinMaxFallback(
                                    prevReadings,
                                    {
                                        highest: highestCandidate,
                                        lowest: lowestCandidate,
                                        value: valueCandidate,
                                        timestamp: data?.ts,
                                    }
                                );

                            return { ...s, readings: updatedReading };
                        }
                        return s;
                    });
                });
            }
        } catch (error) {
            console.error("HubDetails: Error refreshing sensor reading:", error);
        }
    };

    // Handle refresh sensor click
    const handleRefreshSensor = async (
        e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
        sensor: any
    ) => {
        const target = e.currentTarget as HTMLButtonElement;
        if (!target) return;
        
        const sensorId = `${sensor.ldsu_uuid}-${sensor.SAID}`;
        target.dataset.sensorId = sensorId;
        target.classList.add("refresh-state");
        
        try {
            await refreshOneSensor(sensor.ldsu_uuid, sensor.SAID);
        } finally {
            const element = document.querySelector(`.refresh-sensor[data-sensor-id="${sensorId}"]`) as HTMLButtonElement;
            if (element) {
                element.classList.remove("refresh-state");
                delete element.dataset.sensorId;
            }
        }
    };

    // Single-row controller refresh: per-device reading2. Bulk refresh still uses getAllLastBusReading.
    const refreshOneController = async (controllerUID: string, said: number) => {
        if (!id || !controllerUID) return;

        try {
            const { data, status } = await getSensorLastReading(id, controllerUID);
            if (!isHttpSuccess(status)) return;

            const parsed = getControllerStatusFromReading2(data, said);
            if (!parsed) return;

            const { status: updatedStatus, timestamp: updatedTimestamp } = parsed;

            setControllers((prevControllers: any[]) =>
                prevControllers.map((c: any) => {
                    if (c.UID === controllerUID && c.said === said) {
                        return {
                            ...c,
                            readings: {
                                value: updatedStatus,
                                status: updatedStatus,
                                timestamp: updatedTimestamp,
                            },
                        };
                    }
                    return c;
                })
            );
        } catch (error) {
            console.error("HubDetails: Error refreshing controller status:", error);
        }
    };

    // Handle refresh controller click
    const handleRefreshController = async (
        e: React.MouseEvent,
        controllerName: string
    ) => {
        e.stopPropagation();

        const target = e.currentTarget as HTMLButtonElement | null;

        // Find the controller by name
        const controller = controllers.find((c: any) => c.name === controllerName);
        const controllerUID = controller?.UID;
        if (!controllerUID) {
            console.error("HubDetails: Controller not found for refresh");
            return;
        }

        if (target) {
            target.classList.add("refresh-state");
        }

        try {
            await refreshOneController(controllerUID, controller?.said || 0);
        } finally {
            if (target) {
                target.classList.remove("refresh-state");
            }
        }
    };

    const mapControllersWithLatestReadings = (controllersList: any[], allDevices: any[]): any[] => {
        return controllersList.map((controller: any) => {
            const device = allDevices.find((d: any) => d.UID === controller.UID);
            if (!device) {
                return controller;
            }
            const updatedStatus = get(device.value, `[${controller.said || 0}]`, null);
            const updatedTimestamp = device.ts;
            return {
                ...controller,
                readings: {
                    value: updatedStatus,
                    status: updatedStatus,
                    timestamp: updatedTimestamp,
                },
            };
        });
    };

    // Refresh all controller statuses
    const refreshAllControllerStatuses = async () => {
        if (!id || !controllers.length) return;
        
        setIsRefreshing(true);
        try {
            // Check all buses (1-3) to get all controller readings
            const readingPromises = [];
            for (let busNumber = 1; busNumber <= 3; busNumber++) {
                readingPromises.push(getAllLastBusReading(id, busNumber.toString()));
            }
            const readingResults = await Promise.all(readingPromises);
            
            // Collect all device readings from all buses
            const allDevices: any[] = [];
            for (const result of readingResults) {
                if (isHttpSuccess(result.status) && result.data.devices) {
                    allDevices.push(...result.data.devices);
                }
            }
            
            // Update all controllers with their latest readings
            setControllers((prevControllers: any[]) =>
                mapControllersWithLatestReadings(prevControllers, allDevices)
            );
        } catch (error) {
            console.error("HubDetails: Error refreshing all controller statuses:", error);
        } finally {
            setIsRefreshing(false);
        }
    };

    /* istanbul ignore next -- edit button is intentionally disabled in current UI flow */
    const toggleHubNameEdit = () => {
        setIsHubNameEditing(!isHubNameEditing);
    };

    /* istanbul ignore next -- edit button is intentionally disabled in current UI flow */
    const toggleHubNameUpdate = () => {
        setIsHubNameEditing(false);
        // Update the hub object with new name
        if (hub) {
            setHub({ ...hub, name: hubName });
        }
    };

    // Tab detection logic
    const getTabType = getSensorsControllersTabType;

    const renderCustomFiltersForTab = (tabKey: string): React.ReactNode => {
        // Always show filters on Sensors/Controllers tabs themselves, even when there is no data.
        if (tabKey === 'sensors') {
            return (
                <SensorTypeDropdown
                    sensorTypes={sensorTypes}
                    onChange={setSelectedSensorTypes}
                    selected={selectedSensorTypes}
                />
            );
        }

        if (tabKey === 'controllers') {
            return (
                <ActuatorTypeDropdown
                    actuatorTypes={actuatorTypes}
                    onChange={setSelectedActuatorTypes}
                    selected={selectedActuatorTypes}
                />
            );
        }

        return null;
    };

    const handleRowClick = (row: any, tabType?: string) => {
        switch (tabType) {
            case 'sensors':
                history.push({
                    pathname: `/network-details/${id}/hub-details/${hubIdFromParams}/sensor-details/${row.ldsu_uuid}-${row.SAID}`,
                    state: {
                        sensorDetails: row,
                        gatewayName: "Network Gateway",
                        portNumber: "1",
                        UUID: id,
                        sensorTimeRange: {
                            range: timeRange.range,
                            startDate: timeRange.startDate.valueOf(),
                            endDate: timeRange.endDate.valueOf(),
                        },
                    }
                });
                break;
            case 'controllers':
                if (row.UID != null && row.said != null) {
                    history.push(`/network-details/${id}/hub-details/${hubIdFromParams}/controller-details/${row.UID}-${row.said}`);
                }
                break;
            default:
                break;
        }
    };

    const rowToContext: NetworkRowToContext = useMemo(
        () => ({ page: "hub", networkId: id ?? "", hubId: hubIdFromParams ?? "" }),
        [id, hubIdFromParams]
    );
    const getRowTo = useCallback(
        (row: any, tabType?: string) => getRowToForContext(rowToContext, row, tabType),
        [rowToContext]
    );

    // Preprocess data for controllers tab - merge status and lastActive
    const processTabData = (key: string, data: any[]) => {
        if (getTabType('controllers', data)) {
            return data.map((row: any) => ({
                ...row,
                status: `${row.status} ${row.lastActive}`,
                // Remove lastActive since it's now part of status
            }));
        }
        return data;
    };

    
    const renderOnOffHighlighted = (text: string) => renderOnOffHighlightedCommon(text);

    // Generate breadcrumbs based on context
    const generateBreadcrumbs = () => {
        const networkDetailsTo = id && id !== "undefined" ? `/network-details/${id}` : "";

        return [
            {
                to: "/networks",
                label: "All Networks",
                active: false,
            },
            {
                to: networkDetailsTo,
                label: "Network Details",
                active: false,
            },
            {
                to: "",
                label: "Hub Details",
                active: true,
            },
        ];
    };

    // Show "Hub not found" state when no hub data
    if (!hub) {
        return (
            <ContentWrapper isLoading={isPageLoading}>
                <div className="page-content">
                    <Container fluid>
                        <Row>
                            <Col sm={6} className="HubDetails-header">
                                <Row>
                                    <Col>
                                        <h5 className="page-title">{customTitle || hubName || hubIdFromParams || "Hub Details"}</h5>
                                    </Col>
                                    {showBreadcrumbs && (
                                        <Col sm="12">
                                            <Breadcrumb className="w-100">
                                                {generateBreadcrumbs().map((item) => (
                                                    <Breadcrumb.Item
                                                        key={item.to || item.label}
                                                        linkAs={item.active ? undefined : Link}
                                                        linkProps={item.active ? undefined : { to: item.to }}
                                                        active={item.active}
                                                    >
                                                        {item.label}
                                                    </Breadcrumb.Item>
                                                ))}
                                            </Breadcrumb>
                                        </Col>
                                    )}
                                </Row>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={8}>
                                <div className="hub-not-found-panel" style={{
                                    backgroundColor: '#2f3a51',
                                    borderRadius: '8px',
                                    padding: '24px',
                                    marginBottom: '20px'
                                }}>
                                    <div style={{
                                        color: '#ff3860',
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        marginBottom: '16px'
                                    }}>
                                        {isPageLoading ? 'Loading hub data...' : 'Hub not found.'}
                                    </div>
                                    {!isPageLoading && (
                                    <div style={{ color: '#a0a0a0' }}>
                                        The requested hub could not be found in this network.
                                    </div>
                                    )}
                                </div>
                            </Col>
                            <Col sm={4}>
                                <div className="form-box mb-2" style={{ paddingLeft: '30px', paddingRight: '30px', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                                    <div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "none" }}>
                                            <h5 className="flex-grow-1" style={{ color: '#ffffff' }}>Status</h5>
                                            <div style={{ textAlign: "right" }}>
                                                <span
                                                    style={{
                                                        color: "#ff3860",
                                                        marginRight: 8,
                                                        fontSize: '16px'
                                                    }}
                                                >
                                                    &#9679;
                                                </span>
                                                <span style={{ color: '#ffffff' }}>Not found</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ContentWrapper>
        );
    }

    // Helper to get default column keys for each tab type when no data is available
    const getDefaultColumnKeys = (tabKey: string): string[] => {
        switch (tabKey) {
            case 'sensors':
                return ['name', 'type', 'reading', 'minmax', 'lastactive', 'alert'];
            case 'controllers':
                return ['name', 'type', 'status'];
            default:
                return [];
        }
    };

    // Helper to get data source for column generation (prioritize tabData, fallback to state)
    const getDataForColumns = (tabKey: string) => {
        // First try tabData (filtered data)
        if (tabData[tabKey] && tabData[tabKey].length > 0) {
            return tabData[tabKey];
        }
        // Fallback to state variables (unfiltered data)
        let stateData: any[] = [];
        if (tabKey === 'sensors') stateData = processedSensors;
        else if (tabKey === 'controllers') stateData = processedControllers;
        
        if (stateData && Array.isArray(stateData) && stateData.length > 0) {
            return stateData;
        }
        // If no data, create a sample object with default keys for this tab type
        const defaultKeys = getDefaultColumnKeys(tabKey);
        if (defaultKeys.length > 0) {
            const sampleObject: any = {};
            for (const key of defaultKeys) {
                sampleObject[key] = null;
            }
            return [sampleObject];
        }
        return null;
    };

    const handleAlertToggle = (ldsuUuid: string, said: number) => {
        const isChecked = returnCheckboxStatus(ldsuUuid, said);

        if (isChecked) {
            updateAlertStatus(ldsuUuid, said);
            return;
        }

        walletModalTrigger(() => updateAlertStatus(ldsuUuid, said));
    };

    const getColumnLabel = (col: string, tabKey: string): React.ReactNode =>
        getNetworkColumnLabel({
            col,
            tabKey,
            tabLabels,
            isRefreshing,
            refreshIcon,
            onRefreshSensors: refreshAllSensorReadings,
            onRefreshControllers: refreshAllControllerStatuses,
        });

    const getColumnRender = (col: string, tabKey: string): ColumnRenderer | undefined => {
        const colLowerForRender = col.toLowerCase();

        if ((colLowerForRender === "name" || colLowerForRender === "sensor_name") && tabKey === "sensors") {
            const renderer = createCommonSensorNameRenderer(tabKey, handleRowClick, getRowTo);
            return (row: any) => renderer(row);
        }

        if (colLowerForRender === "reading" && tabKey === "sensors") {
            return createReadingRenderer(col);
        }

        if ((colLowerForRender === "minmax" || colLowerForRender.includes("minmax")) && tabKey === "sensors") {
            return createMinMaxRenderer(col);
        }

        if (colLowerForRender === "name" && tabKey === "controllers") {
            return createControllerNameRenderer(col, tabKey, handleRowClick, getRowTo);
        }

        if (colLowerForRender === "lastactive" || colLowerForRender.includes("lastactive")) {
            return createLastActiveRenderer(col, tabKey, handleRefreshSensor);
        }

        if (colLowerForRender === "status") {
            return createStatusRenderer(col, tabKey, handleRefreshController, renderOnOffHighlighted);
        }

        if (colLowerForRender === "alert") {
            return createAlertRenderer(col, handleAlertToggle, returnCheckboxStatus, returnDisabledStatus);
        }

        return (row: any) => row[col];
    };

    // Reusable function to generate columns from data
    const generateColumnsFromData = (data: any[], tabKey: string) => {
        if (!data || data.length === 0) return [];

        return Object.keys(data[0])
            .filter((col: string) => {
                const lower = col.toLowerCase();

                if (tabKey === "controllers" &&
                    (lower === "lastactive" || lower.includes("lastactive") ||
                     lower === "uid" || lower === "said")) {
                    return false;
                }
                if (tabKey === "sensors" &&
                    (lower === "ldsu_uuid" || lower === "said")) {
                    return false;
                }
                if (tabKey === "sensors" && lower === "minmax" && col !== "minMax") {
                    return false;
                }
                if (tabKey === "sensors") {
                    const allowedColumns = ["name", "type", "reading", "minmax", "lastactive", "alert"];
                    return allowedColumns.includes(lower);
                }
                return true;
            })
            .map((col) => {
                const lowerCol = col.toLowerCase();
                const isSensorTab = tabKey === "sensors";
                let sortKey: string | undefined;

                if (isSensorTab) {
                    if (lowerCol === "reading") {
                        sortKey = "readingSortValue";
                    } else if (lowerCol === "minmax" || lowerCol.includes("minmax")) {
                        sortKey = "minMaxSortValue";
                    } else if (lowerCol === "lastactive" || lowerCol.includes("lastactive")) {
                        sortKey = "readings.timestamp";
                    } else if (lowerCol === "alert") {
                        sortKey = "alertSortValue";
                    }
                } else if (tabKey === "controllers") {
                    if (lowerCol === "name" || lowerCol === "type" || lowerCol === "status") {
                        sortKey = col;
                    }
                }

                return {
                    label: getColumnLabel(col, tabKey),
                    key: col,
                    render: getColumnRender(col, tabKey),
                    sortKey,
                };
            });
    };

    const renderSensorNameCell = (value: any, row: any) => {
        const to = getRowTo(row, "sensors");
        return (
            <span style={{ display: "flex", alignItems: "center" }}>
                <SensorIcon
                    sensorType={row.type || row.sensor_type || "Generic Analog Input"}
                    style={{ marginRight: 15 }}
                />
                <Link
                    to={to}
                    onClick={to === "#" ? (e) => {
                        e.preventDefault();
                        handleRowClick(row, "sensors");
                    } : undefined}
                >
                    {value}
                </Link>
            </span>
        );
    };

    const renderSensorReadingCell = (value: any, row: any) => {
        const formattedValue = formatSensorReadingCellValue(row, value);
        return <span style={{ color: "#00d1b2" }}>{formattedValue}</span>;
    };

    const renderSensorMinMaxCell = (value: any) => (
        <span>
            {value || "-"}
        </span>
    );

    const renderSensorLastActiveCell = (value: any, row: any) => (
        <span>
            {value}
            <IconButton
                disableRipple
                className={
                    isRefreshing
                        ? "mr-2 bigRefresh refresh-animation"
                        : "mr-2 bigRefresh"
                }
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", marginLeft: 8 }}
                aria-label="Refresh sensor reading"
                data-sensor-id={`${row.ldsu_uuid}-${row.SAID}`}
                onClick={(e) => {
                    e.stopPropagation();
                    handleRefreshSensor(e, row);
                }}
                onKeyDown={(e) => {
                    e.stopPropagation();
                    handleRefreshSensor(e, row);
                }}
            >
                <img
                    src={refreshIcon}
                    alt="refresh"
                />
            </IconButton>
        </span>
    );

    const renderSensorAlertCell = (value: any, row: any) => (
        <HoverAuthorizeTooltip permission="gateway:update">
            <div>
                <Form.Check
                    type="switch"
                    id={`switch-${row.ldsu_uuid}-${row.SAID}`}
                    label=""
                    checked={returnCheckboxStatus(
                        row.ldsu_uuid,
                        row.SAID
                    )}
                    disabled={returnDisabledStatus(
                        row.ldsu_uuid,
                        row.SAID
                    )}
                    onChange={() => handleAlertToggle(row.ldsu_uuid, row.SAID)}
                />
            </div>
        </HoverAuthorizeTooltip>
    );

    const getEmptyMessageForTab = (tabKey: string): string => {
        if (tabKey === "sensors") {
            return "No Sensors to display";
        }

        if (tabKey === "controllers") {
            return "No Actuators to display";
        }

        return "No data to display";
    };

    const tabData: { [key: string]: any[] } = {
        sensors: filteredSensorList.length > 0 ? filteredSensorList : processedSensors,
        controllers: processTabData('controllers', processedControllers),
    };

    return (
        <ContentWrapper isLoading={isPageLoading}>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col sm={6} className="HubDetails-header">
                            <Row>
                                <Col>
                                    <h5 className="page-title">{customTitle || hub.name}</h5>
                                </Col>
                                {showBreadcrumbs && (
                                    <Col sm="12">
                                        <Breadcrumb className="w-100">
                                            {generateBreadcrumbs().map((item) => (
                                                <Breadcrumb.Item
                                                    key={item.to || item.label}
                                                    linkAs={item.active ? undefined : Link}
                                                    linkProps={item.active ? undefined : { to: item.to }}
                                                    active={item.active}
                                                >
                                                    {item.label}
                                                </Breadcrumb.Item>
                                            ))}
                                        </Breadcrumb>
                                    </Col>
                                )}
                            </Row>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={6}>
                            <div className="hub-summary rounded shadow-sm">
                                <div className="hub-info-group">
                                    <div>
                                        <Form>
                                            <div className="form-box mb-2">
                                                <Form.Row>
                                                    <Col md="12">
                                                        <Form.Label>
                                                            Hub Name
                                                        </Form.Label>
                                                    </Col>
                                                    <Col
                                                        lg={10}
                                                        xs={12}
                                                        className="mb-2"
                                                    >
                                                        <Form.Group className="mb-0">
                                                            <Form.Control
                                                                type="text"
                                                                required
                                                                aria-label="hub-name"
                                                                defaultValue={hubName}
                                                                disabled={!isHubNameEditing}
                                                                onChange={(e) => {
                                                                    setHubName(e.target.value);
                                                                }}
                                                                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                                    if (e.key === "Enter") {
                                                                        e.preventDefault();
                                                                        toggleHubNameUpdate();
                                                                    }
                                                                }}
                                                            />
                                                        </Form.Group>
                                                    </Col>

                                                    <Col lg={2} xs="12">
                                                        {isHubNameEditing ? (
                                                            <Button
                                                                type="button"
                                                                className="w-100"
                                                                onClick={toggleHubNameUpdate}
                                                            >
                                                                Update
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                type="button"
                                                                className="w-100"
                                                                disabled={true}
                                                                onClick={toggleHubNameEdit}
                                                            >
                                                                EDIT
                                                            </Button>
                                                        )}
                                                    </Col>
                                                </Form.Row>
                                            </div>
                                        </Form>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} className="d-flex flex-column">
                            <div className="form-box hub-summary rounded shadow-sm" style={{ padding: '0.5rem 30px' }}>
                                <div className="hub-info-group">
                                    <div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">Status</h6>
                                            {(() => {
                                                const isOnline = hub.status === "Online";
                                                const isOffline = hub.status === "Offline";
                                                const dotColor = isOnline ? "#00d1b2" : "#ff3860";
                                                let label = "Unknown";
                                                if (isOffline) {
                                                    label = "Offline";
                                                } else if (isOnline) {
                                                    label = "Online";
                                                }

                                                return (
                                                    <>
                                                        <span
                                                            style={{
                                                                color: dotColor,
                                                                marginRight: 8,
                                                            }}
                                                        >
                                                            &#9679;
                                                        </span>
                                                        {label}
                                                    </>
                                                );
                                            })()}
                                                <IconButton
                                                    disableRipple
                                                    className={
                                                        isRefreshing
                                                            ? "mr-2 ml-2 bigRefresh refresh-animation"
                                                            : "mr-2 ml-2 bigRefresh"
                                                    }
                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                    aria-label="Refresh hub status"
                                                    onClick={() => {
                                                        updateHubStatus();
                                                    }}
                                                    onKeyDown={() => updateHubStatus()}
                                                >
                                                    <img
                                                        src={refreshIcon}
                                                        alt="refresh"
                                                    />
                                                </IconButton>
                                        </div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">UUID</h6>
                                            <h6 className="ginfo float-right">{hub.uuid || '-'}</h6>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={12}>
                            <div className="page-tabs hubdetails-tabs mt-3">
                                <Tab.Container
                                    id="hub-details-tabs"
                                    activeKey={activeTab}
                                    onSelect={(k) => setActiveTab(k || "sensors")}
                                >
                                    <Row>
                                        <Col sm={12}>
                                            <Nav className="flex-nowrap" style={{ whiteSpace: 'nowrap' }}>
                                                {tabKeys.map((key) => (
                                                    <Nav.Item key={key} style={{ width: '300px' }}>
                                                        <Nav.Link eventKey={key}>
                                                            <h6>
                                                                {tabLabels[key]}{" "}
                                                                <span className="count">
                                                                    {tabData[key] ? tabData[key].length : 0}
                                                                </span>
                                                            </h6>
                                                        </Nav.Link>
                                                    </Nav.Item>
                                                ))}
                                            </Nav>
                                        </Col>
                                        <Col sm={12}>
                                            <Tab.Content>
                                                {tabKeys.map((key) => (
                                                    <Tab.Pane key={key} eventKey={key}>
                                                        <div style={{ borderRadius: 8, padding: 0 }}>
                                                            {key === 'sensors' && !IS_BASIC_PLAN && (
                                                                <WalletAlertComponent showWarningWhen="LowBalance" />
                                                            )}
                                                            <TabTableWithSearch
                                                                data={tabData[key] || []}
                                                                emptyMessage={getEmptyMessageForTab(key)}
                                                                columns={
                                                                    (() => {
                                                                        // Try to get data from tabData first, then fallback to state
                                                                        const dataForColumns = getDataForColumns(key);
                                                                        if (dataForColumns) {
                                                                            return generateColumnsFromData(dataForColumns, key);
                                                                        }
                                                                        // If no data available at all, return empty array (no headers will show)
                                                                        return [];
                                                                    })()
                                                                }
                                                                placeholder={`Search...`}
                                                                onRowClick={handleRowClick}
                                                                getRowTo={getRowTo}
                                                                tabType={key}
                                                                showArrowColumn={true}
                                                                customRenderers={
                                                                    getTabType('sensors', tabData[key] || []) ? {
                                                                        sensor_name: renderSensorNameCell,
                                                                        reading: renderSensorReadingCell,
                                                                        minMax: renderSensorMinMaxCell,
                                                                        lastactive: renderSensorLastActiveCell,
                                                                        alert: renderSensorAlertCell,
                                                                    } : {}
                                                                }
                                                                customFilters={renderCustomFiltersForTab(key)}
                                                            />
                                                        </div>
                                                    </Tab.Pane>
                                                ))}
                                            </Tab.Content>
                                        </Col>
                                    </Row>
                                </Tab.Container>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
            <LoadingModal showModal={isAlertLoading} />
        </ContentWrapper>
    );
};

export default HubDetails; 